import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material'
import { Timer } from '@mui/icons-material'
import type { Task } from '@/types'
import { useTaskStore } from '@/store/useTaskStore'
import { useAuthStore } from '@/store/useAuthStore'
import { useAchievements } from '@/hooks/useAchievements'
import { useNotifications } from '@/hooks/useNotifications'

interface TaskCompletionDialogProps {
  open: boolean
  onClose: () => void
  task: Task | null
}

export default function TaskCompletionDialog({
  open,
  onClose,
  task,
}: TaskCompletionDialogProps) {
  const [timeTaken, setTimeTaken] = useState('')
  const [notes, setNotes] = useState('')
  const completeTask = useTaskStore((state) => state.completeTask)
  const currentUserId = useAuthStore((state) => state.currentUserId)
  const { checkAchievementsForUser } = useAchievements()
  const { sendNotification } = useNotifications()

  const handleComplete = async () => {
    if (!task || !currentUserId) return

    await completeTask(
      task.id,
      currentUserId,
      timeTaken ? parseInt(timeTaken) : undefined,
      notes || undefined
    )

    // Send completion notification
    sendNotification(
      'task_reminder',
      'Task Completed!',
      `You completed "${task.title}"`,
      { task_id: task.id }
    )

    // Check for achievement unlocks
    await checkAchievementsForUser(currentUserId)

    setTimeTaken('')
    setNotes('')
    onClose()
  }

  if (!task) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete Task</DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <Typography variant="h6">{task.title}</Typography>

          <Typography variant="body2" color="text.secondary">
            Estimated: {task.estimated_duration} minutes
          </Typography>

          <TextField
            label="Actual Time Taken (minutes)"
            type="number"
            value={timeTaken}
            onChange={(e) => setTimeTaken(e.target.value)}
            placeholder={task.estimated_duration.toString()}
            InputProps={{
              startAdornment: <Timer sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            inputProps={{ min: 1 }}
            fullWidth
          />

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Any notes about this task completion..."
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleComplete} variant="contained" color="success">
          Complete Task
        </Button>
      </DialogActions>
    </Dialog>
  )
}
